import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Iter208 — Modal di creazione rapida da vista calendario.
 * Crea un appuntamento SUBITO CONFERMATO con singolo slot.
 * Iter208.49 — Supporto ricorrenza settimanale/mensile + property picker
 * inline (quando `properties` è passato e `propertyId` non è fissato).
 *
 * Props:
 *  - open, onClose
 *  - propertyId (opzionale se `properties` fornito)
 *  - properties (opzionale): [{_id, address}]
 *  - initialDate: Date
 *  - propertyAddress, studioAddress (per select luogo)
 *  - onCreated(appointment|list)
 */
function toLocalDateInput(d) {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function toIsoLocal(date, time) {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = (time || "10:00").split(":").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0);
  return dt.toISOString();
}

function addYears(d, years) {
  const nd = new Date(d);
  nd.setFullYear(nd.getFullYear() + years);
  return nd;
}

export default function QuickCreateAppointmentModal({
  open, onClose, propertyId, properties, initialDate, propertyAddress, studioAddress, onCreated,
  editAppointment = null,
  hidePropertyPicker = false,
}) {
  const [pickedPropId, setPickedPropId] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [duration, setDuration] = useState(60);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Ricorrenza
  const [recurring, setRecurring] = useState(false);
  const [frequency, setFrequency] = useState("weekly");
  const [untilDate, setUntilDate] = useState("");

  const isEdit = !!editAppointment;
  const todayStr = toLocalDateInput(new Date());

  useEffect(() => {
    if (open) {
      if (editAppointment) {
        // Iter208.62 — Modalità Edit: pre-compila dai dati esistenti.
        const slot = editAppointment.slots?.[0] || {};
        const startIso = slot.start_at || editAppointment.start_at;
        const dt = startIso ? new Date(startIso) : new Date();
        setTitle(editAppointment.title || "");
        setDate(toLocalDateInput(dt));
        const hh = String(dt.getHours()).padStart(2, "0");
        const mm = String(dt.getMinutes()).padStart(2, "0");
        setTime(`${hh}:${mm}`);
        setDuration(Number(slot.duration_min || editAppointment.duration_min) || 60);
        setDescription(editAppointment.description || "");
        setRecurring(false);
        setPickedPropId(editAppointment.property_id || propertyId || "");
      } else {
        const d = initialDate instanceof Date ? initialDate : new Date();
        setTitle("");
        setDate(toLocalDateInput(d));
        setTime("10:00");
        setDuration(60);
        setDescription("");
        setRecurring(false);
        setFrequency("weekly");
        setUntilDate(toLocalDateInput(addYears(d, 1)));
        setPickedPropId(propertyId || (properties?.[0]?._id ?? ""));
      }
    }
  }, [open, initialDate, propertyId, properties, editAppointment]);

  const canSubmit = useMemo(() => {
    const pid = propertyId || pickedPropId;
    // Iter208.70 — Se il picker è nascosto (appuntamento "Personale" dal
    // calendario globale) NON richiediamo pid a livello di UI: usiamo il
    // primo immobile disponibile per soddisfare il vincolo backend, ma
    // l'utente non deve vederlo nel form.
    if (!hidePropertyPicker && !pid) return false;
    if (!title.trim()) return false;
    if (!date || !time) return false;
    if (recurring && !untilDate) return false;
    return true;
  }, [title, date, time, recurring, untilDate, propertyId, pickedPropId, hidePropertyPicker]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || submitting) return;
    // Iter208.62 — Blocca esplicitamente date passate lato client.
    if (date < todayStr) {
      toast.error("Non puoi creare un appuntamento in una data passata");
      return;
    }
    setSubmitting(true);
    try {
      const pid = propertyId || pickedPropId;
      if (isEdit) {
        // Iter208.62 — Modalità Edit: PUT /appointments/{aid}
        const slotStart = toIsoLocal(date, time);
        const existingSlotId = editAppointment?.slots?.[0]?.id || null;
        const payload = {
          title: title.trim(),
          description: description.trim(),
          slots: [{ id: existingSlotId || undefined, start_at: slotStart, duration_min: Number(duration) || 60 }],
        };
        await api.put(`/appointments/${editAppointment._id}`, payload);
        toast.success("Appuntamento aggiornato");
        onCreated?.({ appointment: { ...editAppointment, title: title.trim() } });
        onClose?.();
        return;
      }
      const payload = {
        title: title.trim(),
        start_at: toIsoLocal(date, time),
        duration_min: Number(duration) || 60,
        description: description.trim(),
        location_type: "property",
        location_value: "",
      };
      if (recurring) {
        payload.recurring = { frequency, until: untilDate };
      }
      const r = await api.post(`/properties/${pid}/appointments/quick`, payload);
      if (r.data?.recurring) {
        toast.success(`Creati ${r.data.count} appuntamenti ricorrenti`);
      } else {
        toast.success("Appuntamento creato");
      }
      onCreated?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella creazione");
    } finally {
      setSubmitting(false);
    }
  };

  const showPropertyPicker = !hidePropertyPicker && !propertyId && Array.isArray(properties) && properties.length > 0 && !isEdit;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="quick-appointment-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica appuntamento" : "Nuovo appuntamento"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {showPropertyPicker && (
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Immobile *</label>
              <select
                value={pickedPropId}
                onChange={(e) => setPickedPropId(e.target.value)}
                className="w-full border rounded px-2 py-2 text-sm bg-white"
                data-testid="quick-appointment-property"
              >
                {properties.map((p) => (
                  <option key={p._id} value={p._id}>{p.address || p.title || p._id}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Titolo *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Es. Sopralluogo con acquirenti"
              data-testid="quick-appointment-title"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Data *</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={todayStr}
                data-testid="quick-appointment-date"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Ora *</label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="quick-appointment-time"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Durata (minuti)</label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full border rounded px-2 py-2 text-sm bg-white"
              data-testid="quick-appointment-duration"
            >
              <option value={30}>30 minuti</option>
              <option value={45}>45 minuti</option>
              <option value={60}>1 ora</option>
              <option value={90}>1 ora e 30</option>
              <option value={120}>2 ore</option>
              <option value={180}>3 ore</option>
            </select>
          </div>

          {/* Iter208.49 — Ricorrenza (nascosta in edit) */}
          {!isEdit && (
          <div className="border-t pt-3 space-y-2">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={recurring}
                onChange={(e) => setRecurring(e.target.checked)}
                data-testid="quick-appointment-recurring-toggle"
              />
              Ripeti appuntamento
            </label>
            {recurring && (
              <div className="grid grid-cols-2 gap-2 pl-5" data-testid="quick-appointment-recurring-panel">
                <div>
                  <label className="text-[11px] font-semibold text-slate-600 mb-1 block">Frequenza</label>
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    className="w-full border rounded px-2 py-2 text-sm bg-white"
                    data-testid="quick-appointment-frequency"
                  >
                    <option value="weekly">Ogni settimana</option>
                    <option value="monthly">Ogni mese</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-600 mb-1 block">Fino al</label>
                  <Input
                    type="date"
                    value={untilDate}
                    onChange={(e) => setUntilDate(e.target.value)}
                    min={date}
                    data-testid="quick-appointment-until"
                  />
                </div>
              </div>
            )}
          </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Note (opzionale)</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Dettagli per i partecipanti"
              data-testid="quick-appointment-description"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onClose?.()} data-testid="quick-appointment-cancel">
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit || submitting}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="quick-appointment-submit"
            >
              {submitting ? (isEdit ? "Salvo…" : "Creazione…") : (isEdit ? "Salva modifiche" : (recurring ? "Crea ricorrenti" : "Crea appuntamento"))}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
